import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Trash2, Mail, Phone, Home } from "lucide-react";

const ROLE_COLORS = {
  admin: "bg-primary text-primary-foreground",
  board: "bg-secondary text-secondary-foreground",
  owner: "bg-accent text-accent-foreground",
};

export default function Directory() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const del = async (uid) => {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const filtered = users.filter((u) =>
    !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div>
        <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
          <Users className="w-4 h-4" /> Community directory
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">Directory</h1>
        <p className="text-foreground/70 mt-2">
          {user?.role === "admin" ? "Manage all HOA members." : "Everyone in the community."}
        </p>
      </div>

      <input
        type="search"
        placeholder="Search by name or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-md h-11 px-4 rounded-full border border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary/40"
        data-testid="directory-search-input"
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((u) => (
          <div key={u.id} className="hoa-card p-5" data-testid={`user-${u.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-lg">
                {u.name?.[0]?.toUpperCase() || "?"}
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[u.role]}`}>{u.role}</span>
            </div>
            <div className="font-semibold text-foreground">{u.name}</div>
            {u.unit_number && (
              <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                <Home className="w-3.5 h-3.5" /> Unit {u.unit_number}
              </div>
            )}
            <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
              <Mail className="w-3.5 h-3.5" /> {u.email}
            </div>
            {u.phone && (
              <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                <Phone className="w-3.5 h-3.5" /> {u.phone}
              </div>
            )}
            {user?.role === "admin" && u.id !== user.id && (
              <div className="mt-4 pt-4 border-t border-border flex gap-2">
                <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                  <SelectTrigger className="h-9 text-xs flex-1" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="board">Board</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => del(u.id)} className="text-muted-foreground hover:text-destructive transition-colors p-2" data-testid={`del-user-${u.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
